import React from "react";
import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from "react-leaflet";
import L from "leaflet";
import "./MapComponent.scss";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

/* Encuadra el mapa en las chinchetas que haya en cada momento: sin esto el
   mapa se queda con el zoom fijo que le pasa la página (a veces zoom 1,
   vista mundial) aunque solo haya un puñado de centros con coordenadas. */
const FitBounds = ({ points }) => {
    const map = useMap();

    useEffect(() => {
        if (!points || points.length === 0) return;

        if (points.length === 1) {
            map.setView(points[0], 12);
            return;
        }

        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }, [points, map]);

    return null;
};

const MapComponent = ({
    data = [],
    center = [40.0, -4.0],
    zoom = 6,
    height = "100%",
    width = "100%",
    modalContent = null,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [modalTitle, setModalTitle] = useState("Detalle del centro");
    const detailButtonText = "Ver detalles";

    const mapData = (data || []).filter((x) => {
        const ejeX = x.ejeX ?? x.EjeX ?? x.latitud ?? x.Latitud;
        const ejeY = x.ejeY ?? x.EjeY ?? x.longitud ?? x.Longitud;

        return ejeX != null && ejeY != null && Number(ejeX) !== 0 && Number(ejeY) !== 0;
    });

    // Leaflet quiere [latitud, longitud]. En esta base de datos EjeX guarda la
    // longitud y EjeY la latitud (al revés de lo que sugiere el nombre), así
    // que aquí van cruzados a propósito.
    const markerPositions = mapData.map((marker) => [
        Number(marker.ejeY ?? marker.EjeY ?? marker.latitud ?? marker.Latitud),
        Number(marker.ejeX ?? marker.EjeX ?? marker.longitud ?? marker.Longitud),
    ]);

    const handleMarkerClick = (marker) => {
        setSelectedMarker(marker);
        setIsModalOpen(true);
        setModalTitle(marker.nombre || "Detalle del centro");
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedMarker(null);
    };

    const renderPopupContent = (marker) => {
        // Si hay contenido personalizado, usarlo
        // if (customPopupContent && typeof customPopupContent === "function") {
        //     return customPopupContent(marker);
        // }

        // Contenido por defecto
        return (
            <div className="marker-popup">
                <h4>{marker.nombre}</h4>
                <p>{marker.direccion}</p>
                {modalContent && (
                    <Button
                        variant="filled"
                        onClick={() => handleMarkerClick(marker)}
                    >
                        {detailButtonText}
                    </Button>
                )}
            </div>
        );
    };

    return (
        <div className="map-component">
            <MapContainer
                center={center}
                zoom={zoom}
                style={{ height, width }}
                className="map-container"
            >
                <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Mapa">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satélite">
                        <TileLayer
                            attribution='&copy; <a href="https://www.esri.com">Esri</a>'
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                    </LayersControl.BaseLayer>
                </LayersControl>

                <FitBounds points={markerPositions} />

                {mapData.map((marker, index) => (
                    <Marker
                        key={marker.id || marker.codObra}
                        position={markerPositions[index]}
                    >
                        <Popup width={200}>{renderPopupContent(marker)}</Popup>
                    </Marker>
                ))}
            </MapContainer>

            <Modal isOpen={isModalOpen} onClose={closeModal} title={modalTitle}>
                {modalContent && selectedMarker && modalContent(selectedMarker)}
            </Modal>
        </div>
    );
};

export default MapComponent;
